/**
 * CollectionScreen - Shows agents in a collection
 */

import { useEffect, useState } from 'react';
import type { Collection } from '../types/agent';
import { AgentCard } from '../components/AgentCard';
import { AgentCardSkeleton } from '../components/Skeleton';
import { getCollection } from '../lib/api';

interface CollectionScreenProps {
  slug: string;
  onAgentClick: (agentId: string) => void;
  onBack: () => void;
}

export function CollectionScreen({ slug, onAgentClick, onBack }: CollectionScreenProps) {
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      try {
        const data = await getCollection(slug);
        setCollection(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetch();
  }, [slug]);

  if (loading) {
    return (
      <div className="space-y-4 animate-screen-fade">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-2 rounded-full skeleton-shimmer" />
          <div className="h-6 skeleton-shimmer rounded w-32 mx-auto mb-1" />
          <div className="h-4 skeleton-shimmer rounded w-48 mx-auto" />
        </div>
        <AgentCardSkeleton />
        <AgentCardSkeleton />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="text-center py-12 animate-screen-fade">
        <p className="text-4xl mb-4">😿</p>
        <h2 className="text-lg font-semibold mb-4">Collection not found</h2>
        <button onClick={onBack} className="px-6 py-2 bg-[#0088CC] text-white rounded-xl">Go Back</button>
      </div>
    );
  }

  return (
    <div className="pb-6 animate-screen-fade">
      <div className="text-center mb-6">
        <p className="text-5xl mb-2">{collection.emoji}</p>
        <h1 className="text-xl font-bold">{collection.name}</h1>
        {collection.description && (
          <p className="text-sm text-[var(--tg-theme-hint-color)] mt-1">{collection.description}</p>
        )}
        <p className="text-xs text-[var(--tg-theme-hint-color)] mt-2">
          {collection.agents?.length || 0} apps
        </p>
      </div>

      {collection.agents && collection.agents.length > 0 ? (
        <div className="space-y-3 card-stagger">
          {collection.agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} onSelect={onAgentClick} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 animate-screen-fade">
          <div className="text-5xl mb-3 animate-float">📂</div>
          <h3 className="text-lg font-semibold mb-1">This collection is empty</h3>
          <p className="text-sm text-[var(--tg-theme-hint-color)]">
            Apps will appear here once they're added to this collection
          </p>
        </div>
      )}

      {/* Telegram BackButton handles navigation */}
    </div>
  );
}
